<template>
	<div class="editor-wrapper">
		<div
			ref="editor"
			:style="`min-height: ${minHeight}; max-height: ${maxHeight}; overflow: scroll;`"
			@click="handleClick"
		></div>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";

import Quill, { QuillOptionsStatic } from "quill";

import "quill/dist/quill.snow.css";

type EditorDataType = {
	editor: Quill | null;
};

export default defineComponent({
	name: "Editor",
	props: {
		value: {
			type: String,
			default: ""
		},
		placeholder: {
			type: String,
			default: ""
		},
		options: {
			type: Object as PropType<QuillOptionsStatic>,
			default: () => ({})
		},
		maxHeight: {
			type: String,
			default: "200px"
		},
		minHeight: {
			type: String,
			default: "200px"
		},
		readOnly: {
			type: Boolean,
			default: false
		}
	},
	data(): EditorDataType {
		return {
			editor: null
		};
	},
	watch: {
		value: {
			handler(newVal: string) {
				if (this.editor && this.editor.root.innerHTML !== newVal) {
					this.editor.root.innerHTML = newVal;
				}
			}
		}
	},
	mounted() {
		this.init();
	},
	methods: {
		init() {
			const _this = this;
			const options: QuillOptionsStatic = {
				placeholder: this.placeholder,
				theme: "snow",
				readOnly: this.readOnly,
				...this.options
			};
			this.editor = new Quill(this.$refs.editor as InstanceType<typeof HTMLInputElement>, options);
			this.editor.root.innerHTML = this.value;
			this.editor.on("text-change", function () {
				return _this.update();
			});
		},
		update() {
			if (this.editor) {
				this.$emit("update:value", this.editor.getText() ? this.editor.root.innerHTML : "");
				this.$emit("input", this.editor.getText() ? this.editor.root.innerHTML : "");
			}
		},
		handleClick() {
			this.editor?.focus();
		}
	}
});
</script>
<style lang="scss">
.editor-wrapper,
.editor-viewer {
	color: var(--gray-100);
	.ql-snow .ql-picker {
		color: var(--gray-100);
	}
	.ql-toolbar.ql-snow {
		border: 1px solid var(--gray-700);
		font-family: var(--body-font);
		background-color: var(--gray-500);
	}

	.ql-container {
		font-family: var(--body-font);
	}
	.ql-container.ql-snow {
		border: 1px solid var(--gray-700);
	}
	.ql-snow .ql-stroke {
		stroke: var(--gray-100);
	}
	.ql-snow.ql-toolbar button:hover,
	.ql-snow .ql-toolbar button:hover,
	.ql-snow.ql-toolbar button:focus,
	.ql-snow .ql-toolbar button:focus,
	.ql-snow.ql-toolbar button.ql-active,
	.ql-snow .ql-toolbar button.ql-active,
	.ql-snow.ql-toolbar .ql-picker-label:hover,
	.ql-snow .ql-toolbar .ql-picker-label:hover,
	.ql-snow.ql-toolbar .ql-picker-label.ql-active,
	.ql-snow .ql-toolbar .ql-picker-label.ql-active,
	.ql-snow.ql-toolbar .ql-picker-item:hover,
	.ql-snow .ql-toolbar .ql-picker-item:hover,
	.ql-snow.ql-toolbar .ql-picker-item.ql-selected,
	.ql-snow .ql-toolbar .ql-picker-item.ql-selected {
		color: var(--primary-300);
	}
	.ql-editor.ql-blank::before {
		font-style: normal;
		color: var(--gray-300);
	}
	.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-label {
		border-color: var(--gray-700);
	}
	.ql-snow.ql-toolbar button:hover .ql-stroke,
	.ql-snow .ql-toolbar button:hover .ql-stroke,
	.ql-snow.ql-toolbar button:focus .ql-stroke,
	.ql-snow .ql-toolbar button:focus .ql-stroke,
	.ql-snow.ql-toolbar button.ql-active .ql-stroke,
	.ql-snow .ql-toolbar button.ql-active .ql-stroke,
	.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
	.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke,
	.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
	.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
	.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke,
	.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke,
	.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
	.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
	.ql-snow.ql-toolbar button:hover .ql-stroke-miter,
	.ql-snow .ql-toolbar button:hover .ql-stroke-miter,
	.ql-snow.ql-toolbar button:focus .ql-stroke-miter,
	.ql-snow .ql-toolbar button:focus .ql-stroke-miter,
	.ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
	.ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
	.ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
	.ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
	.ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
	.ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
	.ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
	.ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
	.ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
	.ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
		stroke: var(--primary-300);
	}
	.ql-toolbar.ql-snow .ql-picker.ql-expanded .ql-picker-options {
		border-color: transparent; //var(--gray-700);
		border-radius: 4px;
	}
	.ql-snow .ql-picker-options {
		background-color: var(--gray-500);
		padding: 12px 16px;
		-moz-box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
		-webkit-box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
		box-shadow: 0 0 4px rgba(0, 0, 0, 0.35);
	}

	strong {
		font-weight: bold;
	}
	em {
		font-style: italic;
	}
	u {
		text-decoration: underline;
	}
	s {
		text-decoration: line-through;
	}
	blockquote {
		border-left: 4px solid var(--dashboard-border-light);
		margin-bottom: 5px;
		margin-top: 5px;
		padding-left: 16px;
	}
	.ql-snow .ql-editor {
		h1 {
			font-size: 20px;
			font-weight: bold;
			line-height: 30px;
		}

		h2 {
			font-size: 18px;
			font-weight: bold;
			line-height: 27px;
		}

		h3 {
			font-size: 16px;
			font-weight: bold;
			line-height: 24px;
		}
	}
	ol {
		list-style-type: decimal;
		li {
			counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
			counter-increment: list-0;
			list-style-type: none;
			&:before {
				margin-left: -1.5em;
				margin-right: 0.3em;
				text-align: right;
				content: counter(list-0, decimal) ". ";
				display: inline-block;
				white-space: nowrap;
				width: 1.2em;
			}
		}
	}
	ul {
		padding-left: 1.5em;
		margin: 0;
		padding: 0;
		counter-reset: list-1 list-2 list-3 list-4 list-5 list-6 list-7 list-8 list-9;
		li {
			list-style-type: none;
			&:before {
				margin-left: -1.5em;
				margin-right: 0.3em;
				text-align: right;
				content: "\2022";
				display: inline-block;
				white-space: nowrap;
				width: 1.2em;
			}
		}
	}
	.ql-snow .ql-editor pre.ql-syntax,
	pre.ql-syntax {
		background-color: var(--gray-900);
		white-space: pre-wrap;
		margin-bottom: 5px;
		margin-top: 5px;
		padding: 5px 10px;
	}
}
</style>
